import { useEffect, useMemo, useState } from "react";
import { FiCheck, FiCopy, FiClock } from "react-icons/fi";
import { formatBRL, getOrderStatus } from "../../services/billingApi";

const POLL_INTERVAL_MS = 4000;

/** Formata os segundos restantes como MM:SS. */
const formatCountdown = (seconds) => {
  if (seconds <= 0) return "expirado";
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

/**
 * Painel do PIX: QR Code, copia-e-cola, contagem regressiva e confirmação.
 *
 * Enquanto o pagamento não cai, consultamos `/billing/orders/:id/status`, que
 * pergunta ao Mercado Pago em tempo real — assim a tela confirma mesmo se o
 * webhook atrasar.
 */
const PixPanel = ({ checkout, onPaid }) => {
  const [copied, setCopied] = useState(false);
  const [paid, setPaid] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const expiresAt = useMemo(
    () => (checkout?.expiresAt ? new Date(checkout.expiresAt) : null),
    [checkout?.expiresAt]
  );

  // Contagem regressiva até o QR Code expirar.
  useEffect(() => {
    if (!expiresAt) return undefined;

    const tick = () =>
      setSecondsLeft(Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000)));

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  // Polling de confirmação.
  useEffect(() => {
    if (!checkout?.orderId || paid) return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const status = await getOrderStatus(checkout.orderId);
        if (cancelled) return;

        if (status?.paid) {
          setPaid(true);
          onPaid?.(status);
        }
      } catch {
        // Falha de rede momentânea: a próxima rodada tenta de novo.
      }
    };

    const timer = setInterval(poll, POLL_INTERVAL_MS);
    poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [checkout?.orderId, paid, onPaid]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(checkout.qrCode || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  if (paid) {
    return (
      <div className="checkout-pix checkout-pix--paid">
        <div className="checkout-pix__success-icon">
          <FiCheck />
        </div>
        <h4>Pagamento confirmado!</h4>
        <p className="checkout__muted">Tudo liberado. Já pode fechar esta janela.</p>
      </div>
    );
  }

  const isExpired = secondsLeft !== null && secondsLeft <= 0;

  return (
    <div className="checkout-pix">
      {checkout.qrCodeBase64 && !isExpired && (
        <img
          className="checkout-pix__qr"
          src={`data:image/png;base64,${checkout.qrCodeBase64}`}
          alt="QR Code do PIX"
        />
      )}

      <p className="checkout-pix__amount">{formatBRL(checkout.amountCents)}</p>

      {secondsLeft !== null && (
        <p className={`checkout-pix__timer${isExpired ? " checkout-pix__timer--expired" : ""}`}>
          <FiClock /> {isExpired ? "QR Code expirado" : `Expira em ${formatCountdown(secondsLeft)}`}
        </p>
      )}

      {isExpired ? (
        <p className="checkout__muted">
          Feche e gere um novo PIX para concluir o pagamento.
        </p>
      ) : (
        <>
          <p className="checkout__muted">
            Abra o app do seu banco, escolha PIX e escaneie o QR Code — ou use o código abaixo.
          </p>

          <div className="checkout-pix__code">
            <code>{checkout.qrCode}</code>
          </div>

          <button type="button" className="button button--primary checkout__submit" onClick={handleCopy}>
            {copied ? <FiCheck /> : <FiCopy />}
            {copied ? "Código copiado" : "Copiar código PIX"}
          </button>

          <p className="checkout-pix__waiting">
            <span className="checkout-pix__pulse" />
            Aguardando confirmação do pagamento...
          </p>
        </>
      )}
    </div>
  );
};

export default PixPanel;
